import type vi from "locales/vi";

const en: Record<keyof typeof vi, string> = {
  // Sidebar
  "sidebar.brand": "OmniSport",
  "nav.classesMgmt": "Classes",
  "nav.sessions": "Sessions",
  "nav.members": "Students",
  "nav.staff": "Staff",
  "nav.schedule": "Schedule",
  "nav.teachingCourses": "Teaching courses",
  "nav.teachingSchedule": "Teaching schedule",
  "nav.assignedClasses": "Assigned classes",
  "nav.dashboard": "Dashboard",
  "nav.signIn": "Sign in",

  // Navbar
  "navbar.pages": "Pages",
  "navbar.search": "Search...",
  "navbar.notification": "Notifications",
  "navbar.markAllRead": "Mark all read",
  "navbar.noNotifications": "No new notifications",
  "navbar.sendNotification": "Send notification",
  "navbar.notificationSent": "Notification sent successfully",
  "navbar.notificationFailed": "Failed to send notification",
  "navbar.greeting": "Hello",
  "navbar.profileSettings": "Profile settings",
  "navbar.newsletterSettings": "Newsletter settings",
  "navbar.logout": "Log out",

  // Dashboard Widgets
  "widget.earnings": "Earnings",
  "widget.spendThisMonth": "Spend this month",
  "widget.sales": "Sales",
  "widget.yourBalance": "Your balance",
  "widget.newTasks": "New tasks",
  "widget.totalProjects": "Total projects",

  // Dashboard Components
  "dashboard.systemStats": "System statistics",
  "dashboard.centerStats": "Center statistics",
  "dashboard.totalUsers": "Total users",
  "dashboard.totalStudents": "Students",
  "dashboard.totalTrainers": "Trainers",
  "dashboard.totalTeachers": "Class managers",
  "dashboard.totalManagers": "Center managers",
  "dashboard.totalCenters": "Centers",
  "dashboard.totalClasses": "Classes",
  "dashboard.totalSessions": "Sessions",
  "dashboard.totalSpent": "Total spent",
  "dashboard.thisMonth": "This month",
  "dashboard.weeklyRevenue": "Weekly revenue",
  "dashboard.dailyTraffic": "Daily traffic",
  "dashboard.visitors": "Visitors",
  "dashboard.yourPieChart": "Your pie chart",
  "dashboard.monthly": "Monthly",
  "dashboard.yearly": "Yearly",
  "dashboard.weekly": "Weekly",
  "dashboard.yourFiles": "Your files",
  "dashboard.system": "System",
  "dashboard.tasks": "Tasks",
  "dashboard.checkTable": "Check table",
  "dashboard.complexTable": "Complex table",

  // Table Headers
  "table.name": "NAME",
  "table.status": "STATUS",
  "table.date": "DATE",
  "table.progress": "PROGRESS",
  "table.quantity": "QUANTITY",
  "table.tech": "TECH",
  "table.artworks": "ARTWORKS",
  "table.rating": "RATING",
  "table.columnsTable": "4-Columns table",
  "table.seeAll": "See all",

  // Table Status
  "status.approved": "Approved",
  "status.disable": "Disable",
  "status.error": "Error",

  // Marketplace / Classes
  "marketplace.trending": "Trending classes",
  "marketplace.art": "Art",
  "marketplace.music": "Music",
  "marketplace.collection": "Collection",
  "marketplace.sports": "Sports",
  "marketplace.recentlyAdded": "Recently added",
  "marketplace.discoverTitle": "Discover and join amazing sports classes",
  "marketplace.discoverDesc":
    "Step into the dynamic world of sports. Discover the latest classes or start your journey!",
  "marketplace.discoverNow": "Discover now",
  "marketplace.watchVideo": "Watch video",
  "marketplace.history": "History",

  // Profile
  "profile.posts": "Posts",
  "profile.followers": "Followers",
  "profile.following": "Following",
  "profile.generalInfo": "General information",
  "profile.education": "Education",
  "profile.languages": "Languages",
  "profile.department": "Department",
  "profile.workHistory": "Work history",
  "profile.organization": "Organization",
  "profile.birthday": "Birthday",
  "profile.notifications": "Notifications",
  "profile.allProjects": "All projects",
  "profile.seeProductDetails": "See product details",
  "profile.yourStorage": "Your storage",
  "profile.storageDesc": "Supervise your drive space in the easiest way",
  "profile.uploadFiles": "Upload files",
  "profile.uploadHint": "PNG, JPG and GIF files are allowed",
  "profile.completeProfile": "Complete your profile",
  "profile.completeProfileDesc":
    "Stay on the pulse of distributed projects with an online whiteboard to plan, coordinate and discuss",
  "profile.publishNow": "Publish now",

  // Profile Notifications
  "profile.notif.itemComment": "Item comment notifications",
  "profile.notif.buyerReview": "Buyer review notifications",
  "profile.notif.ratingReminder": "Rating reminders notifications",
  "profile.notif.meetups": "Meetups near you notifications",
  "profile.notif.companyNews": "Company news notifications",
  "profile.notif.newLaunches": "New launches and projects",
  "profile.notif.monthlyChanges": "Monthly product changes",
  "profile.notif.newsletter": "Subscribe to newsletter",
  "profile.notif.emailFollow": "Email me when someone follows me",

  // Auth - Sign In
  "auth.signIn": "Sign in",
  "auth.signInSubtitle": "Enter your email and password to sign in!",
  "auth.signInGoogle": "Sign in with Google",
  "auth.or": "or",
  "auth.email": "Email*",
  "auth.emailPlaceholder": "mail@omnisport.com",
  "auth.password": "Password*",
  "auth.passwordPlaceholder": "Min. 8 characters",
  "auth.keepLoggedIn": "Keep me logged in",
  "auth.forgotPassword": "Forgot password?",
  "auth.notRegistered": "Not registered yet?",
  "auth.createAccount": "Create an account",

  "auth.signingIn": "Signing in...",
  "auth.loginError": "Login failed",
  "auth.networkError":
    "Unable to connect to the server. Please check your network connection.",
  "auth.invalidCredentials": "Incorrect username or password",
  "auth.identifier": "Email, phone, or username*",
  "auth.identifierPlaceholder": "Email, phone, or username",
  "auth.signInPhoneSubtitle":
    "Sign in with your email, phone number, or username",

  // Class Management
  "class.title": "Class management",

  // Schedule
  "schedule.title": "Class schedule",
  "schedule.today": "Today",
  "schedule.month": "Month",
  "schedule.week": "Week",
  "schedule.day": "Day",
  "schedule.list": "List",
  "schedule.createEvent": "Create schedule",
  "schedule.editEvent": "Edit schedule",
  "schedule.eventName": "Class name",
  "schedule.eventNamePlaceholder": "e.g. Basic swimming A1",
  "schedule.startTime": "Start",
  "schedule.endTime": "End",
  "schedule.coach": "Class manager",
  "schedule.coachPlaceholder": "Enter class manager name",
  "schedule.location": "Location",
  "schedule.locationPlaceholder": "e.g. pool A, court B...",
  "schedule.color": "Color",
  "schedule.time": "Time",
  "schedule.cancel": "Cancel",
  "schedule.create": "Create",
  "schedule.save": "Save",
  "schedule.delete": "Delete",
  "schedule.edit": "Edit",

  "class.createBtn": "Create class",
  "class.createTitle": "Create new class",
  "class.className": "Class name",
  "class.classNamePlaceholder": "Enter class name",
  "class.colName": "CLASS NAME",
  "class.colSport": "SPORT",
  "class.colManager": "MANAGER",
  "class.colCoach": "CLASS MANAGER",
  "class.colStudents": "STUDENTS",
  "class.colSchedule": "SCHEDULE",
  "class.colStatus": "STATUS",
  "class.maxStudents": "Max students",
  "class.sportPlaceholder": "e.g. swimming, yoga...",
  "class.managerPlaceholder": "Enter manager name",
  "class.coachPlaceholder": "Enter class manager name",
  "class.schedulePlaceholder": "e.g. Mon, Wed, Fri - 5:00 PM",
  "class.statusOpen": "Open",
  "class.statusFull": "Full",
  "class.statusClosed": "Closed",
  "class.cancel": "Cancel",
  "class.create": "Create",
  "class.backToList": "Back to list",
  "class.studentList": "Student list",
  "class.noStudents": "No students yet",

  // Staff Management
  "staff.title": "Staff management",
  "staff.colName": "NAME",
  "staff.colRole": "ROLE",
  "staff.colPhone": "PHONE",
  "staff.colEmail": "EMAIL",
  "staff.colStatus": "STATUS",
  "staff.colJoinDate": "JOIN DATE",

  // Profile Settings
  "nav.settings": "My profile",
  "nav.userSessions": "User sessions",
  "nav.staffAttendance": "Timesheet",
  "nav.leaveRequests": "Leave requests",
  "settings.title": "Profile settings",
  "settings.personalInfo": "Personal information",
  "settings.identityInfo": "Identity information",
  "settings.contactInfo": "Contact information",
  "settings.physicalInfo": "Physical information",
  "settings.avatar": "Avatar",
  "settings.changeAvatar": "Change photo",
  "settings.removeAvatar": "Remove photo",
  "settings.fullName": "Full name",
  "settings.fullNamePlaceholder": "Enter your full name",
  "settings.nickname": "Nickname",
  "settings.nicknamePlaceholder": "Enter your nickname",
  "settings.gender": "Gender",
  "settings.male": "Male",
  "settings.female": "Female",
  "settings.other": "Other",
  "settings.dob": "Date of birth",
  "settings.dobPlaceholder": "dd/mm/yyyy",
  "settings.hometown": "Hometown",
  "settings.hometownPlaceholder": "Enter your hometown",
  "settings.bio": "Bio",
  "settings.bioPlaceholder": "Write a few words about yourself...",
  "settings.height": "Height (cm)",
  "settings.heightPlaceholder": "e.g. 170",
  "settings.weight": "Weight (kg)",
  "settings.weightPlaceholder": "e.g. 65",
  "settings.idCardFront": "ID card front",
  "settings.idCardBack": "ID card back",
  "settings.idCardNumber": "ID card number",
  "settings.idCardNumberPlaceholder": "Enter your ID card number",
  "settings.idCardDate": "Issue date",
  "settings.idCardPlace": "Issue place",
  "settings.idCardPlacePlaceholder": "Enter issue place",
  "settings.uploadImage": "Upload image",
  "settings.dragOrClick": "Drag & drop or click to upload",
  "settings.imageHint": "PNG, JPG max 5MB",
  "settings.email": "Email",
  "settings.emailPlaceholder": "email@omnisport.com",
  "settings.phone": "Phone number",
  "settings.phonePlaceholder": "0xxx xxx xxx",
  "settings.address": "Address",
  "settings.addressPlaceholder": "Enter your full address",
  "settings.save": "Save changes",
  "settings.cancel": "Cancel",
  "settings.edit": "Edit profile",
  "settings.noImage": "No image",
  "settings.saveSuccess": "Saved successfully!",

  // Certificates & Achievements
  "settings.certificates": "Certificates",
  "settings.certificate": "Certificate",
  "settings.noCertificates": "No certificates yet",
  "settings.certNamePlaceholder": "Enter certificate name",
  "settings.addCertificate": "Add certificate",
  "settings.achievements": "Achievements",
  "settings.achievement": "Achievement",
  "settings.noAchievements": "No achievements yet",
  "settings.achievementNamePlaceholder": "Enter achievement name",
  "settings.addAchievement": "Add achievement",
  "settings.attachFiles": "Attach files",

  // Footer
  "footer.poweredBy": "Powered by",
  "footer.allRightsReserved": "All rights reserved by",

  // Accounts
  "nav.reportTemplates": "Session reports",
  "nav.revenue": "Revenue",
  "revenue.pageSubtitle": "Overview of center revenue and recent transactions.",
  "revenue.comingSoonTitle": "Feature in development",
  "revenue.comingSoonDesc":
    "The Revenue screen is part of our future roadmap. Current numbers are mock data for demo purposes only.",
  "revenue.demoDataBadge": "Demo data",
  "revenue.demoDataNotice": "Demo data",
  "revenue.totalRevenue": "Total revenue",
  "revenue.thisMonth": "This month",
  "revenue.activeStudents": "Active students",
  "revenue.avgPerStudent": "Avg per student",
  "revenue.newStudents": "New students",
  "revenue.outstanding": "Outstanding balance",
  "revenue.outstandingHint": "Pending collections",
  "revenue.monthlyChartTitle": "Monthly revenue",
  "revenue.monthlyChartSubtitle": "Last 7 months (VND)",
  "revenue.millionsShort": "{value}M",
  "revenue.recentTransactions": "Recent transactions",
  "revenue.txId": "Transaction",
  "revenue.customer": "Customer",
  "revenue.class": "Class",
  "revenue.amount": "Amount",
  "revenue.method": "Method",
  "revenue.status": "Status",
  "revenue.statusPaid": "Paid",
  "revenue.statusPending": "Pending",
  "revenue.statusRefunded": "Refunded",
  "revenue.methodCash": "Cash",
  "revenue.methodBank": "Bank transfer",
  "revenue.methodCard": "Card",
  "revenue.methodMomo": "MoMo wallet",
  "nav.accounts": "Accounts",
  "accounts.title": "Account management",
  "accounts.create": "Create account",
  "accounts.modalTitle": "Create new account",
  "accounts.cancel": "Cancel",
  "accounts.creating": "Creating...",
  "accounts.createError": "Failed to create account",
  "accounts.empty": "No accounts found",
  "accounts.colName": "FULL NAME",
  "accounts.colCenter": "CENTER",
  "accounts.colEmail": "EMAIL",
  "accounts.colRole": "ROLE",
  "accounts.colCode": "CODE",
  "accounts.colStatus": "STATUS",
  "accounts.active": "Active",
  "accounts.inactive": "Inactive",
  "accounts.fieldName": "Full name",
  "accounts.fieldPassword": "Password",
  "accounts.fieldRole": "Role",
  "accounts.fieldPhone": "Phone number",
  "accounts.fieldCode": "Account code",

  // Common
  "common.close": "Close",

  // Session Notifications Modal
  "notify.sendBtn": "Send notification",
  "notify.modalTitle": "Send notification",
  "notify.titleLabel": "Title",
  "notify.titleOptional": "(leave empty for default)",
  "notify.titlePlaceholder": "e.g. session reminder for today",
  "notify.messageLabel": "Message",
  "notify.messagePlaceholder":
    "e.g. Class starts at 6:00 PM today. Please be on time.",
  "notify.cancelBtn": "Cancel",
  "notify.sendingBtn": "Sending...",
  "notify.sentPrefix": "Notification sent to",
  "notify.studentsSuffix": "students",
  "notify.sendFailed": "Failed to send notification",
  "notify.newForeground": "New notification",

  // Notifications Page
  "notif.title": "Notifications",
  "notif.filterAll": "All",
  "notif.filterUnread": "Unread",
  "notif.filterReminder": "Reminders",
  "notif.filterCancelled": "Cancelled",
  "notif.filterUpdated": "Updated",
  "notif.filterGeneral": "General",
  "notif.markRead": "Mark as read",
  "notif.readAt": "Read at",
  "notif.showing": "Showing",
  "notif.prev": "Previous",
  "notif.next": "Next",
  "notif.viewAll": "View all",

  // Change Password
  "nav.changePassword": "Change password",

  // Common
  "common.soon": "Soon",

  // Export history (Tra cứu học viên)
  "exportHistory.openButton": "Export Excel",
  "exportHistory.title": "Export attendance history",
  "exportHistory.fromDate": "From date",
  "exportHistory.toDate": "To date",
  "exportHistory.scope": "Export scope",
  "exportHistory.scopeAll": "All students in the center",
  "exportHistory.scopeSelected": "Pick specific students",
  "exportHistory.searchPlaceholder": "Search by name, email, code...",
  "exportHistory.submit": "Export Excel file",
  "exportHistory.exporting": "Exporting...",
  "common.cancel": "Cancel",
  "changePassword.title": "Change password",
  "changePassword.currentPassword": "Current password",
  "changePassword.currentPasswordPlaceholder": "Enter current password",
  "changePassword.newPassword": "New password",
  "changePassword.newPasswordPlaceholder":
    "Enter new password (min 8 characters)",
  "changePassword.confirmPassword": "Confirm new password",
  "changePassword.confirmPasswordPlaceholder": "Confirm new password",
  "changePassword.submit": "Change password",
  "changePassword.success": "Password changed successfully",
  "changePassword.errorPasswordMismatch": "Passwords do not match",
  "changePassword.errorPasswordTooShort":
    "Password must be at least 8 characters",
  "changePassword.errorCurrentPasswordRequired":
    "Please enter your current password",

  // Trial Centers
  "trial.navTitle": "Trials",
  "trial.title": "Trial account management",
  "trial.description": "Monitor and manage trial periods for centers.",
  "trial.refresh": "Refresh",
  "trial.filterAll": "All",
  "trial.filterActive": "Active",
  "trial.filterExpiringSoon": "Expiring soon",
  "trial.filterExpired": "Expired",
  "trial.centersFound": "centers",
  "trial.emptyTitle": "No trial centers",
  "trial.emptyDescription":
    "Create a center with a trial period to get started.",
  "trial.colCenter": "Center",
  "trial.colManager": "Manager",
  "trial.colTrialEnd": "Trial end",
  "trial.colDaysRemaining": "Remaining",
  "trial.colUsers": "Users",
  "trial.colStatus": "Status",
  "trial.colActions": "Actions",
  "trial.statusActive": "Active trial",
  "trial.statusExpiringSoon": "Expiring soon",
  "trial.statusExpired": "Expired",
  "trial.statusNoTrial": "No trial",
  "trial.setCustomDate": "Set custom date",
  "trial.setExpired": "Set expired (read-only)",
  "trial.reactivate": "Reactivate (remove read-only)",
  "trial.resetTrial": "Reset trial (restore default)",
  "trial.confirmExpire":
    "All users in this center will be switched to read-only mode. Are you sure?",
  "trial.confirmReset":
    "Remove all trial info? The center will return to normal unrestricted access.",
  "trial.rowsPerPage": "Rows/page",
  "trial.totalCenters": "centers",
  "trial.prev": "Previous",
  "trial.next": "Next",
  "trial.page": "Page",

  // Send Notification Page
  "nav.centerSettings": "Center",
  "nav.sendNotification": "Send notification",
  "sendNotif.title": "Send notification",
  "sendNotif.targetByClass": "By class",
  "sendNotif.targetByUser": "Select users",
  "sendNotif.selectClass": "Select class",
  "sendNotif.selectClassPlaceholder": "-- select a class --",
  "sendNotif.roleLabel": "Role",
  "sendNotif.roleStudent": "Student",
  "sendNotif.roleTeacher": "Class manager",
  "sendNotif.roleTrainer": "Trainer",
  "sendNotif.roleStaff": "Center manager",
  "sendNotif.userList": "User list",
  "sendNotif.selectAll": "Select all",
  "sendNotif.deselectAll": "Deselect all",
  "sendNotif.noUsers": "No users",
  "sendNotif.searchPlaceholder": "Search by name, email...",
  "sendNotif.titleLabel": "Title",
  "sendNotif.titlePlaceholder": "Enter notification title",
  "sendNotif.messageLabel": "Message",
  "sendNotif.messagePlaceholder": "Enter notification message",
  "sendNotif.sendBtn": "Send notification",
  "sendNotif.sendingBtn": "Sending...",
  "sendNotif.success": "Notification sent to {count} users",
  "sendNotif.error": "Failed to send notification",
  "sendNotif.noRecipients": "Please select at least one user",
  "sendNotif.noTitle": "Please enter a title",
  "sendNotif.noMessage": "Please enter a message",
  "sendNotif.selected": "selected",

  // Self Check-in
  "nav.selfCheckin": "Self check-in",
  "selfCheckin.title": "Face self check-in",
  "selfCheckin.selectSession": "Select session",
  "selfCheckin.noSession": "— no session selected —",
  "selfCheckin.cameraOff": "Tap below to open camera",
  "selfCheckin.openCamera": "Open camera",
  "selfCheckin.capture": "Capture",
  "selfCheckin.cancel": "Cancel",
  "selfCheckin.submit": "Confirm check-in",
  "selfCheckin.submitting": "Processing...",
  "selfCheckin.retake": "Retake",
  "selfCheckin.checkedIn": "Checked in successfully!",
  "selfCheckin.checkedOut": "Checked out successfully!",
  "selfCheckin.matchSuccess": "Face verified successfully",
  "selfCheckin.noMatch": "Face not recognized",
  "selfCheckin.error": "An error occurred, please try again",
  "selfCheckin.cameraError": "Cannot access camera",
  "selfCheckin.loadSessionError": "Failed to load sessions",
  "selfCheckin.resultMatched": "Face matched!",
  "selfCheckin.resultNotMatched": "Face not recognized",
  "selfCheckin.score": "Accuracy",
  "selfCheckin.attendanceStatus": "Attendance status",
  "selfCheckin.statusCheckedIn": "Checked in",
  "selfCheckin.statusCheckedOut": "Checked out",
  "selfCheckin.checkedInAt": "Check-in time",
  "selfCheckin.checkedOutAt": "Check-out time",

  // Student schedule dashboard
  "mySchedule.title": "My schedule",
  "mySchedule.loading": "Loading learning data...",
  "mySchedule.notScheduled": "Not scheduled",
  "mySchedule.calendar": "Class calendar",
  "mySchedule.today": "Today",
  "mySchedule.month": "Month",
  "mySchedule.week": "Week",
  "mySchedule.list": "List",
  "mySchedule.myCourses": "My courses (click to view details)",
  "mySchedule.classCode": "Class code",
  "mySchedule.remainingSessions": "Remaining sessions",
  "mySchedule.instructor": "Instructor",
  "mySchedule.notAssigned": "Not assigned",
  "mySchedule.noCourses": "You haven't joined any class yet.",
  "mySchedule.checkin": "Check-in",
  "mySchedule.byDate": "Schedule by date",
  "mySchedule.fromDate": "From date",
  "mySchedule.toDate": "To date",
  "mySchedule.dateRangeError": "Start date must be before end date",
  "mySchedule.loadingSessions": "Loading...",
  "mySchedule.noSessions": "No sessions found",
  "mySchedule.sessionsCount": "sessions",
  "mySchedule.class": "Class",
  "mySchedule.courseDetail": "Course details",
  "mySchedule.courseInfo": "Course information",
  "mySchedule.courseName": "Course name",
  "mySchedule.totalSessions": "Total sessions",
  "mySchedule.center": "Center",
  "mySchedule.noDescription": "No description yet.",
  "mySchedule.instructorInCharge": "Instructor in charge",
  "mySchedule.noBio": "No bio yet.",
  "mySchedule.achievement": "Achievements",
  "mySchedule.certificate": "Certificate",

  // Attendance history
  "nav.attendanceHistory": "Attendance history",
  "attendanceHistory.title": "Attendance history",
  "attendanceHistory.loading": "Loading attendance history...",
  "attendanceHistory.empty": "No attendance history yet.",
  "attendanceHistory.checkedInAt": "Checked in at",
  "attendanceHistory.checkedOutAt": "Checked out at",
  "attendanceHistory.method": "Method",
  "attendanceHistory.status": "Status",
  "attendanceHistory.notes": "Notes",
  "attendanceHistory.methodFace": "Face",
  "attendanceHistory.methodManual": "Manual",
  "attendanceHistory.statusCheckedIn": "Checked in",
  "attendanceHistory.statusCheckedOut": "Checked out",
  "attendanceHistory.statusAbsent": "Absent",
  "attendanceHistory.statusExcused": "Excused absence",
  "attendanceHistory.notAvailable": "—",
  "attendanceHistory.loadError": "Failed to load attendance history",
  "attendanceHistory.loadMore": "Load more",
  "attendanceHistory.totalSessions": "Total sessions",
  "attendanceHistory.checkedInCount": "Checked in",
  "attendanceHistory.absentCount": "Absent",
  "attendanceHistory.excusedCount": "Excused",
  "attendanceHistory.date": "Date",
  "attendanceHistory.detailTitle": "Attendance detail",
  "attendanceHistory.checkInPhoto": "Check-in photo",
  "attendanceHistory.checkOutPhoto": "Check-out photo",
  "attendanceHistory.noPhoto": "No photo",
  "attendanceHistory.checkInLocation": "Check-in location",
  "attendanceHistory.checkOutLocation": "Check-out location",
  "attendanceHistory.noLocation": "No location",
  "attendanceHistory.resolvingAddress": "Resolving address...",
  "attendanceHistory.recordNotFound":
    "No attendance record found for this session.",
  "attendanceHistory.viewOnGoogleMaps": "View on Google Maps",
  "attendanceHistory.tabByMonth": "By month",
  "attendanceHistory.tabAll": "All",
  "attendanceHistory.className": "Class",
  "attendanceHistory.session": "Session",
  "attendanceHistory.monthGroupPrefix": "Month",
  "attendanceHistory.viewDetails": "View details",
  "attendanceHistory.sortNewest": "Newest first",
  "attendanceHistory.sortOldest": "Oldest first",
  "attendanceHistory.emptyMonth": "No sessions in this month.",
  "attendanceHistory.photos": "Photos",
  "attendanceHistory.locations": "Location",
  "attendanceHistory.hasPhoto": "Has check-in photo",

  // Student classes / sessions page
  "nav.classes": "My classes",
  "classesPage.title": "My classes",
  "classesPage.loading": "Loading classes...",
  "classesPage.myCourses": "My courses (click to view sessions)",
  "classesPage.classCode": "Class code",
  "classesPage.remainingSessions": "Remaining sessions",
  "classesPage.instructor": "Instructor",
  "classesPage.notAssigned": "Not assigned",
  "classesPage.noCourses": "You haven't joined any class yet.",
  "classesPage.sessionsTitle": "Sessions (click to view details)",
  "classesPage.noSessions": "This class has no sessions yet.",
  "classesPage.notScheduled": "Not scheduled",
  "classesPage.center": "Center",
  "classesPage.period": "Study period",
  "classesPage.statusOpening": "Enrolling",
  "classesPage.statusOngoing": "Ongoing",
  "classesPage.statusClosed": "Closed",
  "classesPage.instructorInfo": "Instructor info",
  "classesPage.bio": "Bio",
  "classesPage.achievement": "Achievement",
  "classesPage.certificate": "Certificate",
  "classesPage.upcomingSessions": "Upcoming",
  "classesPage.pastSessions": "Past",
  "classesPage.sessionStatusScheduled": "Scheduled",
  "classesPage.sessionStatusActive": "In progress",
  "classesPage.sessionStatusCompleted": "Completed",
  "classesPage.sessionStatusCancelled": "Cancelled",

  // Read-only session detail (opened from a session row)
  "sessionDetail.title": "Session details",
  "sessionDetail.loading": "Loading session details...",
  "sessionDetail.loadError": "Failed to load session details",
  "sessionDetail.time": "Time",
  "sessionDetail.location": "Location",
  "sessionDetail.manager": "Class manager (QLL)",
  "sessionDetail.trainer": "Trainer (HLV)",
  "sessionDetail.noManager": "No class manager assigned",
  "sessionDetail.noTrainer": "No trainer assigned",
  "sessionDetail.checkin": "Open camera to check in",
  "sessionDetail.leaveRequest": "Leave request",
  "sessionDetail.requestLeave": "Request leave",
  "sessionDetail.leaveReasonPlaceholder": "Enter your reason for leave...",
  "sessionDetail.submitLeave": "Submit",
  "sessionDetail.saveLeave": "Save",
  "sessionDetail.cancelLeave": "Cancel",
  "sessionDetail.editLeave": "Edit request",
  "sessionDetail.withdrawLeave": "Withdraw",
  "sessionDetail.leavePending": "Request submitted, pending review",
  "sessionDetail.leaveApproved": "Leave request approved",
  "sessionDetail.leaveRejected": "Request rejected",
  "sessionDetail.leaveError": "Failed to submit leave request",

  // Audit log management
  "audit.navTitle": "Audit logs",
  "audit.title": "System audit logs",
  "audit.subtitle": "Track important actions and changes across the system.",
  "audit.actorEmail": "Actor email",
  "audit.actionType": "Action type",
  "audit.entityType": "Entity type",
  "audit.allResults": "All results",
  "audit.success": "Success",
  "audit.failed": "Failed",
  "audit.dateFrom": "From date",
  "audit.dateTo": "To date",
  "audit.clear": "Clear filters",
  "audit.filter": "Filter logs",
  "audit.total": "Total logs",
  "audit.refresh": "Refresh",
  "audit.time": "Time",
  "audit.actor": "Actor",
  "audit.entity": "Entity",
  "audit.result": "Result",
  "audit.sourceIp": "IP address",
  "audit.details": "Details",
  "audit.loading": "Loading audit logs...",
  "audit.empty": "No matching audit logs found.",
  "audit.loadError": "Unable to load system audit logs.",
  "audit.system": "System",
  "audit.viewDetails": "View details",
  "audit.page": "Page",
  "audit.detailTitle": "Audit log details",
  "audit.requestId": "Request ID",
  "audit.userAgent": "Device / browser",
  "audit.reason": "Reason",
  "audit.errorMessage": "Error message",
  "audit.beforeState": "Before state",
  "audit.afterState": "After state",
  "audit.changedFields": "Changed fields",

  // Session attendance report
  "sessionAttendanceReport.title": "Attendance report",
  "sessionAttendanceReport.subtitle":
    "Session attendance summary grouped by participant type.",
  "sessionAttendanceReport.attendanceRate": "Attendance rate",
  "sessionAttendanceReport.students": "Students",
  "sessionAttendanceReport.staff": "Staff",
  "sessionAttendanceReport.export": "Export Excel report",
  "sessionAttendanceReport.attended": "Attended",
  "sessionAttendanceReport.checkedIn": "Checked in",
  "sessionAttendanceReport.checkedOut": "Checked out",
  "sessionAttendanceReport.absent": "Absent",
  "sessionAttendanceReport.excused": "Excused",
  "sessionAttendanceReport.unmarked": "Not marked",
  "sessionAttendanceReport.person": "Name",
  "sessionAttendanceReport.role": "Role",
  "sessionAttendanceReport.status": "Status",
  "sessionAttendanceReport.checkIn": "Check-in",
  "sessionAttendanceReport.checkOut": "Check-out",
  "sessionAttendanceReport.checkInBadge": "In",
  "sessionAttendanceReport.checkOutBadge": "Out",
  "sessionAttendanceReport.empty": "There are no participants in this group.",

  // Report field - multiple files
  "reportField.addFile": "Add file",
  "reportField.removeFile": "Remove file",
  "reportField.maxFilesReached": "Max file limit reached",
  "reportField.confirmDeleteFile": "Are you sure you want to delete this file?",
  "reportField.uploadedFiles": "Uploaded files",
  "reportField.pendingFiles": "Pending files",

  // Leave requests (class manager review of student leave requests)
  "leaveRequests.title": "Leave requests",
  "leaveRequests.description":
    "Review and approve students' leave requests for the classes you manage.",
  "leaveRequests.classLabel": "Class",
  "leaveRequests.allClasses": "All classes",
  "leaveRequests.noClasses": "No classes",
  "leaveRequests.filterAll": "All",
  "leaveRequests.statusPending": "Pending",
  "leaveRequests.statusApproved": "Approved",
  "leaveRequests.statusRejected": "Rejected",
  "leaveRequests.loading": "Loading requests...",
  "leaveRequests.loadError": "Failed to load leave requests",
  "leaveRequests.reviewError": "Failed to process leave request",
  "leaveRequests.empty": "No leave requests yet.",
  "leaveRequests.submittedAt": "Submitted at",
  "leaveRequests.notScheduled": "Session unknown",
  "leaveRequests.reviewedBy": "Reviewed by",
  "leaveRequests.columnIndex": "#",
  "leaveRequests.columnStudent": "Student",
  "leaveRequests.columnSession": "Class / session",
  "leaveRequests.columnReason": "Reason",
  "leaveRequests.columnSubmittedAt": "Submitted at",
  "leaveRequests.columnReviewer": "Reviewer",
  "leaveRequests.columnStatus": "Status",
  "leaveRequests.columnActions": "Actions",
  "leaveRequests.thisWeekOnly": "This week only",
  "leaveRequests.showAll": "Show all",
  "leaveRequests.approve": "Approve",
  "leaveRequests.reject": "Reject",
  "leaveRequests.resetToPending": "Reset to pending",
  "leaveRequests.notAllowed": "You don't have access to this page.",
};

export default en;
